import { useCallback } from 'react';

import Konva from 'konva';
import { useShallow } from 'zustand/react/shallow';

import { useMagicPenStore } from '@src/stores';

import { constrainInitialPosition, generateUniqueId, toRelativeDimensions, toRelativePosition } from '../../helpers';

import { EMagicPenShape, ICanvasObject, ICreatedBy, IEditingStickyNotes, TSetState } from '@src/types';

export const useStickyNoteTool = (
  setIsDrawing: (drawing: boolean) => void,
  setEditingStickyNote: TSetState<IEditingStickyNotes | null>,
  editingStickyNote: IEditingStickyNotes | null,
  createdBy: ICreatedBy
) => {
  const { brushColor, setSelectedId, selectedId, objects, addObjects } = useMagicPenStore(
    useShallow((state) => ({
      brushColor: state.brushColor,
      setSelectedId: state.setSelectedId,
      selectedId: state.selectedId,
      objects: state.objects,
      addObjects: state.addObjects,
    }))
  );

  const createStickyNote = useCallback(
    async (e: Konva.KonvaEventObject<MouseEvent>, stageRef: React.RefObject<Konva.Stage>) => {
      const clickedOnObject = e.target !== e.target.getStage() && e.target.getType() !== 'Layer';
      const stage = e.target.getStage();
      if (!stage) return;
      const pos = stage.getPointerPosition();
      if (!pos) return;
      if (!clickedOnObject) {
        // Check if there's currently an editing sticky note or a selected sticky note
        const hasSelectedStickyNote =
          selectedId && objects.find((obj) => obj.id === selectedId && obj.type === EMagicPenShape.STICKY_NOTE);

        if (editingStickyNote || hasSelectedStickyNote) {
          // First click: Exit edit mode and unselect
          setSelectedId(null);
          return; // Don't create a new sticky note
        }

        // Second click (or first click with nothing selected): Create new sticky note
        setIsDrawing(true);
        const stickyNoteId = generateUniqueId();

        // Calculate constrained position to keep sticky note within canvas bounds
        const stickyWidth = 100;
        const stickyHeight = 60;

        // Use helper to constrain initial position (offset to center on click)
        const { x: initialX, y: initialY } = constrainInitialPosition(
          pos,
          stickyWidth,
          stickyHeight,
          stageRef.current,
          { x: -50, y: -30 } // Offset to center sticky note on click point
        );

        const containerWidth = stage.width();
        const containerHeight = stage.height();

        const { x: relativeX, y: relativeY } = toRelativePosition(initialX, initialY, containerWidth, containerHeight);
        const { width: relativeWidth, height: relativeHeight } = toRelativeDimensions(
          stickyWidth,
          stickyHeight,
          containerWidth,
          containerHeight
        );

        const stickyNoteObj: ICanvasObject = {
          id: stickyNoteId,
          type: EMagicPenShape.STICKY_NOTE,
          createdBy,
          content: '',
          x: initialX,
          y: initialY,
          width: stickyWidth,
          height: stickyHeight,
          relativeX,
          relativeY,
          relativeWidth,
          relativeHeight,
          backgroundColor: brushColor,
          textColor: '#000000', // Default black text
          isEditing: false,
          fontSize: 14, // Default font size
          hasSynced: false,
        };

        addObjects([stickyNoteObj]);
        // Auto-select the newly created sticky note
        setSelectedId(stickyNoteId);
        setIsDrawing(false);
        // Enter editing mode after a small delay to ensure DOM is ready (make sure the textarea is rendered)
        setTimeout(() => {
          setEditingStickyNote({
            id: stickyNoteId,
            x: initialX,
            y: initialY,
            width: stickyWidth,
            height: stickyHeight,
            content: '',
            fontSize: 14,
            textColor: '#000000',
          });
        }, 10);
      }
    },
    [
      selectedId,
      objects,
      editingStickyNote,
      setIsDrawing,
      createdBy,
      brushColor,
      addObjects,
      setSelectedId,
      setEditingStickyNote,
    ]
  );

  return {
    createStickyNote,
  };
};

export default useStickyNoteTool;
